/**
 * Types for admin events management.
 */

// Event status enum (matches Prisma schema)
export type EventStatus =
  | "DRAFT"
  | "SCHEDULED"
  | "PUBLISHED"
  | "CANCELLED"
  | "ARCHIVED";

// Event visibility enum (matches Prisma schema)
export type EventVisibility = "PUBLIC" | "CHURCH" | "MINISTERS" | "RESTRICTED";

// Gender restriction enum
export type Gender = "MALE" | "FEMALE" | "ANY";

// Organizer role enum
export type OrganizerRole = "OWNER" | "ORGANIZER" | "HELPER";

// Category type
export interface Category {
  id: string;
  code: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  parentId: string | null;
  sortOrder: number;
  isActive: boolean;
  children?: Category[];
  eventCount?: number;
}

// Ministry type
export interface Ministry {
  id: string;
  code: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
  memberCount?: number;
}

// Account (for organizers)
export interface AdminAccount {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string;
}

// Event organizer
export interface EventOrganizer {
  id: string;
  ministry: Ministry | null;
  account: AdminAccount | null;
  role: OrganizerRole;
  canEdit: boolean;
  canManageRsvp: boolean;
  canManageTickets: boolean;
  receiveNotifications: boolean;
}

// Ticket type price
export interface TicketTypePrice {
  id: string;
  currency: string;
  price: string;
}

// Ticket type fee config
export interface TicketTypeFeeConfig {
  id: string;
  type: string;
  prices: { currency: string; amount: string }[];
}

// Ticket type
export interface TicketType {
  id: string;
  name: string;
  description: string | null;
  prices: TicketTypePrice[];
  price: string;
  currency: string;
  maxQuantity: number | null;
  soldCount: number;
  isActive: boolean;
  fees?: { feeConfiguration: TicketTypeFeeConfig }[];
}

// Admin event for list view
export interface AdminEventListItem {
  id: string;
  title: string;
  subtitle: string | null;
  venue: string | null;
  startsAt: string;
  endsAt: string | null;
  imageUrl: string | null;
  status: EventStatus;
  visibility: EventVisibility;
  featured: boolean;
  isActive: boolean;
  categories: Category[];
  createdAt: string;
}

// Full admin event for edit form
export interface AdminEvent {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  venue: string | null;
  address: string | null;
  mapsUrl: string | null;
  startsAt: string;
  endsAt: string | null;
  imageUrl: string | null;
  bannerImageUrl: string | null;
  videoUrl: string | null;
  streamYoutubeUrl: string | null;
  streamVkUrl: string | null;
  telegramChatUrl: string | null;
  inviteText: string | null;
  slogan: string | null;
  program: string | null;
  status: EventStatus;
  visibility: EventVisibility;
  publishAt: string | null;
  featured: boolean;
  genderRestriction: Gender;
  minAge: number | null;
  maxAge: number | null;
  maxAttendees: number | null;
  requiresRsvpConfirmation: boolean;
  homeGroup: { id: string } | null;
  seriesId: string | null;
  isActive: boolean;
  createdAt: string;
  categories: Category[];
  organizers: EventOrganizer[];
  allowedMinistries: { ministry: Ministry }[];
  ticketTypes: TicketType[];
}

// Status labels and colors
export const EVENT_STATUS_CONFIG: Record<
  EventStatus,
  { label: string; color: string; bgColor: string }
> = {
  DRAFT: {
    label: "Черновик",
    color: "text-slate-600",
    bgColor: "bg-slate-100",
  },
  SCHEDULED: {
    label: "Запланировано",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  PUBLISHED: {
    label: "Опубликовано",
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  CANCELLED: {
    label: "Отменено",
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  ARCHIVED: {
    label: "В архиве",
    color: "text-slate-500",
    bgColor: "bg-slate-100",
  },
};

// Visibility labels and colors
export const EVENT_VISIBILITY_CONFIG: Record<
  EventVisibility,
  { label: string; description: string; color: string; bgColor: string }
> = {
  PUBLIC: {
    label: "Публичное",
    description: "Видно всем",
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
  },
  CHURCH: {
    label: "Для церкви",
    description: "Только прихожанам",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  MINISTERS: {
    label: "Для служителей",
    description: "Только служителям",
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  RESTRICTED: {
    label: "Ограниченное",
    description: "Только указанным служениям",
    color: "text-amber-600",
    bgColor: "bg-amber-100",
  },
};

// Gender restriction labels
export const GENDER_CONFIG: Record<Gender, { label: string }> = {
  ANY: { label: "Без ограничений" },
  MALE: { label: "Только мужчины" },
  FEMALE: { label: "Только женщины" },
};

// Organizer role labels
export const ORGANIZER_ROLE_CONFIG: Record<
  OrganizerRole,
  { label: string; description: string }
> = {
  OWNER: { label: "Владелец", description: "Полный контроль" },
  ORGANIZER: {
    label: "Организатор",
    description: "Редактирование, управление RSVP",
  },
  HELPER: { label: "Помощник", description: "Ограниченный доступ" },
};
